import { create } from "zustand";
import { persist } from "zustand/middleware";
import type { MembershipTier, User } from "@/types";
import { createPersistStorage } from "@/lib/storage";
import { signOut as supabaseSignOut, getSession, getCurrentUser } from "@/lib/auth/auth";
import { isDefinitiveInvalidRefreshTokenError } from "@/lib/auth/refresh-token-errors";
import {
  clearAuthBackoffAfterSessionRemoval,
  isAuthBackoffActive,
  hasStoredRefreshToken,
} from "@/lib/supabase/client";
import { getApiUrl } from "@/lib/api/utils";

// Cache for syncSession to prevent redundant calls (multiple components/layouts call sync on load)
let syncSessionCache: { promise: Promise<void>; timestamp: number } | null = null;
const SYNC_SESSION_CACHE_MS = 8000; // 8s cache - prevents auth loop/429 in production when many callers fire

async function clearPersistedAuthArtifacts() {
  try {
    const { getApiUrl } = await import("@/lib/api/utils");
    await fetch(getApiUrl("/api/auth/logout"), {
      method: "POST",
      credentials: "include",
    });
  } catch {
    /* non-fatal */
  }

  try {
    await supabaseSignOut();
  } catch {
    /* non-fatal */
  }

  if (typeof window === "undefined") {
    return;
  }

  try {
    const { clearAuthCookiesClient } = await import("@/lib/auth/logout-flag");

    const ADD_PHONE_KEYS_TO_KEEP = [
      "growthsync-add-phone-dismissed-until",
      "growthsync-add-phone-dismissed-permanently",
    ];
    const keysToRemove: string[] = [];
    for (let i = 0; i < window.localStorage.length; i++) {
      const key = window.localStorage.key(i);
      if (
        key &&
        (key.startsWith("growthsync-") ||
          key.startsWith("auth-") ||
          key.startsWith("profile-") ||
          key.includes("supabase") ||
          key.includes("sb-") ||
          key.includes("auth-token"))
      ) {
        if (!ADD_PHONE_KEYS_TO_KEEP.includes(key)) {
          keysToRemove.push(key);
        }
      }
    }
    keysToRemove.forEach((key) => {
      try {
        window.localStorage.removeItem(key);
      } catch {
        // Ignore
      }
    });

    try {
      window.sessionStorage.clear();
    } catch {
      // Ignore
    }

    clearAuthCookiesClient();

    try {
      const isCapacitor = typeof (window as unknown as { Capacitor?: unknown }).Capacitor !== "undefined";
      if (isCapacitor) {
        const { Preferences } = await import("@capacitor/preferences");
        const supabaseUrl = process.env.NEXT_PUBLIC_SUPABASE_URL || "";
        const projectRef = supabaseUrl.match(/https:\/\/([^.]+)\.supabase\.co/)?.[1];
        const authTokenKey = projectRef ? `sb-${projectRef}-auth-token` : null;
        if (authTokenKey) {
          await Preferences.remove({ key: authTokenKey });
        }
      }
    } catch {
      // Ignore - Capacitor may not be available on web
    }
  } catch {
    /* non-fatal */
  }
}

interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  membershipTier: MembershipTier;
  /** True once Zustand persist has rehydrated from storage - used to prevent logged-out flash on app reopen */
  _hasHydrated: boolean;
  login: (user: User) => void;
  logout: () => Promise<void>;
  /** Clears Supabase session, cookies, and persisted client stores — no navigation (use after invalid/deleted account). */
  clearClientAuthSession: () => Promise<void>;
  updateMembershipTier: (tier: MembershipTier) => void;
  refreshMembership: () => Promise<MembershipTier | null>;
  resolveMembershipOnLogin: (options?: { forceSync?: boolean }) => Promise<MembershipTier>;
  syncSession: () => Promise<void>;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      isAuthenticated: false,
      membershipTier: "free",
      _hasHydrated: false,
      login: (user) =>
        set({
          user,
          isAuthenticated: true,
          membershipTier: user.membershipTier || "free",
        }),
      clearClientAuthSession: async () => {
        syncSessionCache = null;
        clearAuthBackoffAfterSessionRemoval();
        set({
          user: null,
          isAuthenticated: false,
          membershipTier: "free",
        });
        try {
          const { useProfileStore } = await import("./profile-store");
          const profileStore = useProfileStore.getState();
          if (profileStore.setCurrentProfile) {
            profileStore.setCurrentProfile(null as never);
          }
          useProfileStore.setState({ loading: false, error: null });
        } catch (error) {
          console.warn("[Auth Store] Error clearing profile on clearClientAuthSession:", error);
        }
        try {
          const { clearProfileMemoryCache } = await import("@/lib/api/profiles-client");
          clearProfileMemoryCache();
        } catch {
          /* non-fatal */
        }
        await clearPersistedAuthArtifacts();
      },
      logout: async () => {
        if (typeof window !== "undefined") {
          const { setIntentionalLogoutFlag } = await import("@/lib/auth/logout-flag");
          setIntentionalLogoutFlag();
        }
        await get().clearClientAuthSession();
        if (typeof window !== "undefined") {
          window.location.replace("/auth/login");
        }
      },
      updateMembershipTier: (tier) =>
        set((state) => ({
          ...(state.user
            ? {
                user: {
                  ...state.user,
                  membershipTier: tier,
                  updatedAt: new Date().toISOString(),
                },
              }
            : {}),
          membershipTier: tier,
        })),
      refreshMembership: async () => {
        try {
          const response = await fetch(getApiUrl("/api/refresh-membership"), {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
            },
            credentials: "include", // Ensure cookies are sent for authentication
          });

          if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            throw new Error(errorData.error || "Failed to refresh membership");
          }

          const data = await response.json();

          if (data.success && data.membershipTier) {
            const newTier = data.membershipTier as MembershipTier;
            
            // Update auth store
            get().updateMembershipTier(newTier);
            
            // Update profile store if profile exists
            const { useProfileStore } = await import("./profile-store");
            const profileStore = useProfileStore.getState();
            if (profileStore.currentProfile) {
              await profileStore.updateCurrentProfile({
                membershipTier: newTier,
              });
            }
            
            return newTier;
          }
          
          return null;
        } catch (error: any) {
          console.error("Error refreshing membership:", error);
          throw error;
        }
      },
      resolveMembershipOnLogin: async (options) => {
        const currentTier = get().membershipTier;
        try {
          const { authFetch } = await import("@/lib/api/auth-fetch");
          if (options?.forceSync) {
            await authFetch("/api/billing/sync", {
              method: "POST",
              credentials: "include",
            }).catch(() => null);
          }

          const response = await authFetch("/api/me/entitlements", {
            credentials: "include",
          });

          if (!response.ok) {
            return currentTier;
          }

          const entitlements = await response.json();
          const nextTier = (entitlements?.plan_tier || currentTier || "free") as MembershipTier;

          if (nextTier !== currentTier) {
            get().updateMembershipTier(nextTier);
          }

          return nextTier;
        } catch (error) {
          return currentTier;
        }
      },
      syncSession: async () => {
        // CRITICAL: Skip all sync when auth backoff is active (429/400 loop break).
        // Prevents re-triggering refresh/session checks that would spam Supabase.
        if (isAuthBackoffActive()) {
          return;
        }

        // CRITICAL: Skip when user intentionally logged out - do not restore session
        if (typeof window !== "undefined") {
          try {
            const { isIntentionalLogout } = await import("@/lib/auth/logout-flag");
            if (isIntentionalLogout()) {
              return;
            }
          } catch {
            // Ignore
          }
        }

        // OPTIMIZED: Use cache to prevent redundant calls
        const now = Date.now();
        if (syncSessionCache && (now - syncSessionCache.timestamp) < SYNC_SESSION_CACHE_MS) {
          // Return cached promise if still valid
          return syncSessionCache.promise;
        }
        
        // Create new sync promise
        const syncPromise = (async () => {
          // Import diagnostics
          const { logDiagnostic, detectEnvironment } = await import("@/lib/debug/mobile-diagnostics");
          const env = detectEnvironment();
          
          logDiagnostic("syncSession", "Starting session sync", {
            isMobile: env.isMobile,
            isIOS: env.isIOS,
            isTestFlight: env.isTestFlight,
          });
        
        // Get current state first to preserve it
        const currentState = get();
        
        // If we already have a valid authenticated user, only sync if needed
        // Don't aggressively clear sessions
        if (currentState.user && currentState.isAuthenticated) {
          logDiagnostic("syncSession", "User already authenticated, performing sync check", {
            userId: currentState.user.id,
            userEmail: currentState.user.email,
          });
          try {
            // Quick validation - check if Supabase session exists
            // Use a short timeout to avoid blocking
            const session = await Promise.race([
              getSession(),
              new Promise<any>((_, reject) => 
                setTimeout(() => reject(new Error("Timeout")), 2000)
              )
            ]).catch(() => null);
            
            if (session?.access_token) {
              return;
            }
            
            // No Supabase session - only clear if we're certain there's no token (desync). Don't clear on timeout/transient failures.
            if (!hasStoredRefreshToken()) {
              logDiagnostic("syncSession", "AUTH DESYNC: No Supabase session and no token - clearing stale auth", {
                hadUserId: currentState.user?.id,
              });
              console.warn("[syncSession] AUTH DESYNC: No Supabase session and no refresh token. Clearing stale auth state.", {
                userId: currentState.user?.id,
              });
              set({ user: null, isAuthenticated: false, membershipTier: "free" });
              try {
                const { useProfileStore } = await import("./profile-store");
                useProfileStore.setState({ currentProfile: null });
              } catch { /* non-fatal */ }
            }
            return;
          } catch (error: any) {
            logDiagnostic("syncSession", "Error during session validation", {
              error: error instanceof Error ? error.message : String(error),
            });
            
            // Only clear on explicit invalid token errors
            const isInvalidTokenError = isDefinitiveInvalidRefreshTokenError(error);
            
            if (isInvalidTokenError) {
              logDiagnostic("syncSession", "Invalid refresh token detected, clearing auth state");
              console.warn("[syncSession] Invalid refresh token detected, clearing auth state");
              try {
                if (typeof window !== "undefined") {
                  const { logSignOutCall } = await import("@/lib/debug/session-logger");
                  logSignOutCall("AuthStore.syncSession.invalidToken", "Invalid refresh token during sync");
                }
                await supabaseSignOut();
              } catch (signOutError) {
                // Ignore sign out errors
              }
              set({
                user: null,
                isAuthenticated: false,
                membershipTier: "free",
              });
            }
            return;
          }
        }
        
        // No existing auth state - try to restore from Supabase
        // OR: Mobile environment - always do full sync to ensure fresh session
        try {
          // CRITICAL: Skip getSession/getUser when no refresh token in storage - prevents 400/429
          // and SIGNED_OUT spam. Supabase would try to validate/refresh with no token otherwise.
          if (!hasStoredRefreshToken()) {
            return;
          }

          logDiagnostic("syncSession", "Performing full session sync", {
            hasExistingAuth: !!(currentState.user && currentState.isAuthenticated),
            isMobile: env.isMobile,
          });
          
          // CRITICAL: On mobile, use longer timeout and be more forgiving
          // Social apps (Instagram, Facebook, Hinge) never log users out on network errors
          const timeout = env.isMobile ? 8000 : 5000; // Increased timeout for mobile
          
          // CRITICAL: Try to get session - but don't fail on network errors
          // Use getSession() which reads from storage, not getUser() which validates with server
          const session = await Promise.race([
            getSession(),
            new Promise<any>((_, reject) => 
              setTimeout(() => reject(new Error("Timeout")), timeout)
            )
          ]).catch(() => null);
          
          logDiagnostic("syncSession", "Session retrieved", {
            hasSession: !!session,
            hasAccessToken: !!session?.access_token,
            userId: session?.user?.id,
          });
          
          // CRITICAL: Only try to get user if we have a session
          // Don't call getUser() if we don't have a session - it might fail and clear state
          let supabaseUser = null;
          if (session?.access_token) {
            supabaseUser = await Promise.race([
              getCurrentUser(),
              new Promise<any>((_, reject) => 
                setTimeout(() => reject(new Error("Timeout")), timeout)
              )
            ]).catch((error) => {
              // If we have a session, use the user from the session
              return session?.user || null;
            });
          }
          
          logDiagnostic("syncSession", "User retrieved", {
            hasUser: !!supabaseUser,
            userId: supabaseUser?.id,
            userEmail: supabaseUser?.email,
          });

          // No Supabase session/user - only clear if we're certain there's no token (desync).
          if (!session && !supabaseUser) {
            if (currentState.user && currentState.isAuthenticated && !hasStoredRefreshToken()) {
              logDiagnostic("syncSession", "AUTH DESYNC: No Supabase session and no token - clearing stale auth", {
                hadUserId: currentState.user?.id,
              });
              console.warn("[syncSession] AUTH DESYNC: No Supabase session and no refresh token. Clearing stale auth state.", {
                userId: currentState.user?.id,
              });
              set({ user: null, isAuthenticated: false, membershipTier: "free" });
              try {
                const { useProfileStore } = await import("./profile-store");
                useProfileStore.setState({ currentProfile: null });
              } catch { /* non-fatal */ }
            }
            return;
          }

          if (session && supabaseUser) {
            // CRITICAL: If we already have a different user authenticated, don't overwrite
            // This prevents switching users unexpectedly
            if (currentState.user && currentState.user.id !== supabaseUser.id) {
              console.warn(`Session sync found different user (${supabaseUser.id}) but current user is ${currentState.user.id}. Preserving current user.`);
              return; // Don't change the authenticated user
            }
            
            // User has an active Supabase session
            // Fetch profile to get membership tier and account type
            // CRITICAL: Use authFetch to ensure bearer tokens are sent in webview contexts
            try {
              const { authFetch } = await import("@/lib/api/auth-fetch");
              const profileResponse = await authFetch(`/api/profiles/${supabaseUser.id}`, {
                credentials: "include", // Ensure cookies are sent for authentication (fallback)
              });
              if (profileResponse.ok) {
                const profile = await profileResponse.json();
                
                // Double-check: ensure profile belongs to this user
                if (profile.userId !== supabaseUser.id && profile.id !== supabaseUser.id) {
                  console.warn(`Profile userId (${profile.userId}) doesn't match Supabase user (${supabaseUser.id}). Skipping profile update.`);
                  // Still set user but without profile data
                  set({
                    user: {
                      id: supabaseUser.id,
                      email: supabaseUser.email || "",
                      profileId: supabaseUser.id,
                      membershipTier: "free",
                      createdAt: new Date().toISOString(),
                      updatedAt: new Date().toISOString(),
                    },
                    isAuthenticated: true,
                  });
                  return;
                }
                
                // CRITICAL: Determine membership tier - admins automatically get Elite
                const isAdmin = profile.accountType === "admin" || profile.user_role === "admin";
                const membershipTier = isAdmin ? "elite" : (profile.membershipTier || "free");
                
                set({
                  user: {
                    id: supabaseUser.id,
                    email: supabaseUser.email || "",
                    profileId: profile.id || supabaseUser.id,
                    membershipTier,
                    createdAt: profile.createdAt || new Date().toISOString(),
                    updatedAt: profile.updatedAt || new Date().toISOString(),
                  },
                  isAuthenticated: true,
                  membershipTier,
                });

                // Resolve authoritative membership immediately after profile bootstrap.
                // Force sync when profile still says free to avoid stale DB/webhook lag.
                try {
                  await get().resolveMembershipOnLogin({
                    forceSync: membershipTier === "free",
                  });
                } catch {
                  // Non-fatal; keep profile-derived tier.
                }
                
                logDiagnostic("syncSession", "Session sync completed successfully", {
                  userId: supabaseUser.id,
                  email: supabaseUser.email,
                  membershipTier,
                  profileAccountType: profile.accountType,
                  profileUserRole: profile.user_role,
                  isAdmin,
                  effectiveMembershipTier: membershipTier,
                });
                
                // CRITICAL: Refresh entitlements from server (single source of truth)
                // This ensures we have the latest subscription status after login
                try {
                  const { authFetch } = await import("@/lib/api/auth-fetch");
                  const entitlementsResponse = await authFetch("/api/me/entitlements", {
                    credentials: "include",
                  });
                  if (entitlementsResponse.ok) {
                    const entitlements = await entitlementsResponse.json();
                    if (entitlements.plan_tier && entitlements.plan_tier !== membershipTier) {
                      set({
                        membershipTier: entitlements.plan_tier,
                        ...(get().user ? {
                          user: {
                            ...get().user!,
                            membershipTier: entitlements.plan_tier,
                          },
                        } : {}),
                      });
                    }
                  }
                } catch {
                  // Non-fatal - profile tier is still valid
                }
                
                // CRITICAL: Also update the profile store to ensure UI shows correct profile
                try {
                  const { useProfileStore } = await import("./profile-store");
                  await useProfileStore.getState().fetchCurrentProfile(supabaseUser.id);
                } catch {
                  // Non-fatal - auth is still valid
                }
              } else {
                // Profile doesn't exist yet, but user is authenticated
                // CRITICAL: Don't set a generic user - wait for profile to be created
                // Profile should be created during signup, so this should be rare
                console.warn("[Auth Store] User authenticated but profile not found. Profile should be created during signup.");
                
                // Still set minimal auth state, but don't create a generic "user" profile
                set({
                  user: {
                    id: supabaseUser.id,
                    email: supabaseUser.email || "",
                    profileId: supabaseUser.id,
                    membershipTier: currentState.membershipTier || "free",
                    createdAt: new Date().toISOString(),
                    updatedAt: new Date().toISOString(),
                  },
                  isAuthenticated: true,
                  membershipTier: currentState.membershipTier || "free",
                });
                
                // Try to fetch profile again after a short delay (profile might still be creating)
                // CRITICAL: Use authFetch to ensure bearer tokens are sent in webview contexts
                setTimeout(async () => {
                  try {
                    const { authFetch } = await import("@/lib/api/auth-fetch");
                    const retryResponse = await authFetch(`/api/profiles/${supabaseUser.id}`, {
                      credentials: "include", // Ensure cookies are sent for authentication (fallback)
                    });
                    if (retryResponse.ok) {
                      const retryProfile = await retryResponse.json();
                      const retryMembershipTier = retryProfile.membershipTier || "free";
                      set({
                        user: {
                          id: supabaseUser.id,
                          email: supabaseUser.email || "",
                          profileId: retryProfile.id || supabaseUser.id,
                          membershipTier: retryMembershipTier,
                          createdAt: retryProfile.createdAt || new Date().toISOString(),
                          updatedAt: retryProfile.updatedAt || new Date().toISOString(),
                        },
                        membershipTier: retryMembershipTier,
                      });
                      
                      // Also update profile store
                      const { useProfileStore } = await import("./profile-store");
                      await useProfileStore.getState().fetchCurrentProfile(supabaseUser.id);
                    }
                  } catch (retryError) {
                    console.warn("Profile retry fetch failed:", retryError);
                  }
                }, 2000);
              }
            } catch (error) {
              // If profile fetch fails, still authenticate user - don't clear session
              // But log the error so we know profile fetch failed
              console.error("Failed to fetch profile during session sync (non-fatal):", error);
              
              // Set minimal auth state - profile will be fetched later or user needs to complete onboarding
              set({
                user: {
                  id: supabaseUser.id,
                  email: supabaseUser.email || "",
                  profileId: supabaseUser.id,
                  membershipTier: currentState.membershipTier || "free",
                  createdAt: new Date().toISOString(),
                  updatedAt: new Date().toISOString(),
                },
                isAuthenticated: true,
                membershipTier: currentState.membershipTier || "free",
              });
              
              // Don't create a generic "user" profile in profile store
              // Profile store will remain null until profile is successfully fetched
            }
          }
        } catch (error: any) {
          console.error("Error syncing session:", error);
          
          // Only clear session if it's specifically an invalid refresh token error
          const isInvalidTokenError = isDefinitiveInvalidRefreshTokenError(error);
          
          if (isInvalidTokenError) {
            console.warn("Invalid refresh token detected, clearing auth state");
            try {
              if (typeof window !== "undefined") {
                const { logSignOutCall } = await import("@/lib/debug/session-logger");
                logSignOutCall("AuthStore.syncSession.catch.invalidToken", "Invalid refresh token in catch block");
              }
              await supabaseSignOut();
            } catch (signOutError) {
              // Ignore sign out errors
            }
            set({
              user: null,
              isAuthenticated: false,
              membershipTier: "free",
            });
          }
          // For all other errors, do nothing - preserve existing state or don't set anything
        }
        })();
        
        // Cache the promise
        syncSessionCache = {
          promise: syncPromise,
          timestamp: now,
        };
        
        // Clear cache after promise resolves/rejects
        syncPromise.finally(() => {
          // Keep cache for 500ms after resolve so rapid successive callers (Login, Auth layout, SessionSync) share result
          setTimeout(() => {
            if (syncSessionCache?.promise === syncPromise) {
              syncSessionCache = null;
            }
          }, 500);
        });
        
        return syncPromise;
      },
    }),
    {
      name: "growthsync-auth",
      storage: createPersistStorage(),
      version: 1, // Increment this when you want to force logout on new builds
      partialize: (state) => ({
        user: state.user,
        isAuthenticated: state.isAuthenticated,
        membershipTier: state.membershipTier,
      }),
      migrate: (persistedState: any, version: number) => {
        // If version mismatch, clear auth state (forces re-login)
        if (version !== 1) {
          console.log("[Auth Store] Version mismatch detected, clearing auth state");
          return {
            user: null,
            isAuthenticated: false,
            membershipTier: "free",
          };
        }
        return persistedState;
      },
      // CRITICAL: Don't clear auth on build version changes - this was causing unwanted logouts
      // Only clear if explicitly needed (e.g., breaking auth changes)
      // Build version changes should not force re-login
      onRehydrateStorage: () => (state, err) => {
        if (typeof window !== "undefined") {
          // Mark hydration complete - prevents flash of logged-out state on app reopen
          useAuthStore.setState({ _hasHydrated: true });
          if (state) {
            // Store current build version for reference, but don't clear auth on change
            const currentBuildVersion = process.env.NEXT_PUBLIC_APP_VERSION || "0.1.0";
            const storedBuildVersion = window.localStorage.getItem("growthsync-build-version");
            if (storedBuildVersion && storedBuildVersion !== currentBuildVersion) {
              console.log("[Auth Store] Build version changed (auth preserved)", {
                old: storedBuildVersion,
                new: currentBuildVersion,
              });
            }
            window.localStorage.setItem("growthsync-build-version", currentBuildVersion);
          }
        }
      },
    },
  ),
);

// CRITICAL: Fallback so _hasHydrated becomes true even if onRehydrateStorage never fires.
// On iOS WebView/Capacitor, persist callback can be delayed or dropped. Without this,
// AuthGate stays stuck on LoadingScreen forever (cross-device boot inconsistency).
// Safari and Android WebView have slower persist hydration - use longer fallback.
if (typeof window !== "undefined") {
  const isSafari = /Safari/i.test(navigator.userAgent) && !/Chrome/i.test(navigator.userAgent);
  const isAndroidApp = /Android/i.test(navigator.userAgent) && (window as any).Capacitor?.getPlatform?.() === "android";
  const HYDRATION_FALLBACK_MS = (isSafari || isAndroidApp) ? 450 : 150;
  setTimeout(() => {
    if (!useAuthStore.getState()._hasHydrated) {
      useAuthStore.setState({ _hasHydrated: true });
    }
  }, HYDRATION_FALLBACK_MS);
}

